import React, { FunctionComponent, memo, MouseEvent, useEffect, useMemo } from 'react';

import Card, { CardProps } from '@gojob/ui-bits/Card';
import Flex from '@gojob/ui-bits/Flex';
import Icon from '@gojob/ui-bits/Icon';
import Unhappy from '@gojob/ui-bits/Icons/Unhappy';
import Skeleton from '@gojob/ui-bits/Skeleton';
import Text from '@gojob/ui-bits/Text';
import GatsbyLink from 'gatsby-link';

import Location from 'src/components/JobPreview/Location';
import SalaryChip from 'src/components/JobPreview/SalaryChip';
import WeeklyHours from 'src/components/JobPreview/WeeklyHours';

import useTheme from '@gojob/ui-bits/styles/useTheme';
import searchTracking from 'src/tracking/search';
import useTrackOfferEvent from 'src/tracking/useTrackOfferEvent';
import useTranslation from 'src/translations/useTranslation';

export interface JobPreviewProps extends CardProps<typeof GatsbyLink> {
  id?: string;
  shortId?: string;
  caption?: string;
  position?: number;
  city?: string;
  postalCode?: string;
  weeklyHours?: number;
  salary?: {
    grossHourlyRateIncludingCP?: number;
  };
  loading?: boolean;
  error?: boolean;
}

const useStyles = () => {
  const { boxShadow } = useTheme();
  return useMemo(
    () =>
      ({
        root: {
          boxSizing: 'border-box',
          minWidth: '100%',
          border: 'none',
          textDecoration: 'none',
          outline: 'none',
          ':focus, :active, :hover': {
            boxShadow: boxShadow('shadow', 5),
          },
        },
      } as const),
    [boxShadow]
  );
};

const JobPreview: FunctionComponent<JobPreviewProps> = (props) => {
  const {
    caption: jobLabel = '',
    city = '',
    postalCode = '',
    to = '/#',
    loading,
    weeklyHours,
    salary,
    error,
    onClick,
    position,
    shortId,
    ...other
  } = props;

  const classes = useStyles();
  const { spacing } = useTheme();

  const t = useTranslation();
  const trackOfferEvent = useTrackOfferEvent(props, position);
  useEffect(() => {
    if (!loading && !error) {
      trackOfferEvent('promoView');
    }
  }, [trackOfferEvent, loading, error]);

  const handleOnClick = (e: MouseEvent<HTMLAnchorElement>) => {
    searchTracking.clickOnJobResult();
    trackOfferEvent('promoClick');
    onClick?.(e as any);
  };

  if (error) {
    return (
      <Card
        disabled
        as="button"
        css={classes.root}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        color="white"
        padding={1}
        elevation={3}
        onClick={handleOnClick}
        data-testid="JobPreview"
        {...other}
      >
        <Icon data-testid="ErrorIcon" as={Unhappy} color="icon" size={12} margin={[0, 0, 2]} />
        <Text variant="paragraph">{t('could_not_reach_server')}</Text>
      </Card>
    );
  }

  const element = loading ? ('button' as const) : GatsbyLink;

  return (
    <Card
      as={element}
      to={to}
      css={classes.root}
      flexDirection="column"
      color="white"
      padding={[4, 2, 2, 4]}
      elevation={3}
      onClick={handleOnClick}
      data-testid="JobPreview"
      {...other}
    >
      <Text as="h2" variant="headline4" gutter={2}>
        {loading ? <Skeleton width={spacing(24)} /> : jobLabel}
      </Text>

      <Location loading={loading} city={city} postalCode={postalCode} />

      <Flex margin={[1, 0]} justifyContent="space-between" alignItems="center">
        <WeeklyHours loading={loading} weeklyHours={weeklyHours} />
        <SalaryChip loading={loading} grossHourlyRateWithCP={salary?.grossHourlyRateIncludingCP} />
      </Flex>

      {!loading && (
        <Flex justifyContent="flex-end">
          <Text variant="tertiary" inactive>
            {t('grossRate')}
          </Text>
        </Flex>
      )}
    </Card>
  );
};

export default memo(JobPreview);
